import { isNil, isEmpty } from 'lodash/lang';
import { MediaMixin } from '@loandolphin/dolphin-framework';
import { mapGetters, mapActions } from 'vuex';
import { authComputed } from '@state/helpers';
import getState from '@utils/get-state';
import getEnquiryTitle from '@utils/enquiry/get-enquiry-title';
import getPreviewsData from '@utils/messages/get-previews-data';
import getMessagesData from '@utils/messages/get-messages-data';
import isChatBlocked from '@utils/messages/is-chat-blocked';
import { trackMessageSent } from '@utils/analytics/events';
import { getEnquiryRoute, getMessagePath } from '@enums/route-enum';
import { isInProgress } from '@enums/offer-state-enum';
import ChatBox from '@components/enquiry-messages/chat-box';
import ChatField from '@components/enquiry-messages/chat-field';

export default {
  name: 'enquiry-messages',
  mixins: [MediaMixin],
  components: {
    ChatBox,
    ChatField,
  },
  props: ['uuid', 'providerUuid'],

  data() {
    return {
      currentProviderUuid: this.providerUuid,
      newMessage: null,
      mobile: {
        showAllMessages: false,
      },
    };
  },

  computed: {
    ...authComputed,

    ...mapGetters({
      getEnquiryByUuid: 'connector/getEnquiryByUuid',
      getOffersFromProvider: 'connector/getOffersFromProvider',
      getOverviewsByEnquiry: 'messages/getOverviewsByEnquiry',
      getMessages: 'messages/getMessages',
      getProviderByUuid: 'users/getProviderByUuid',
    }),

    enquiry() {
      return this.getEnquiryByUuid(this.uuid);
    },

    offers() {
      return this.getOffersFromProvider(
        {
          enquiryUuid: this.uuid,
          providerUuid: this.providerUuid,
        },
      );
    },

    overviews() {
      return this.getOverviewsByEnquiry(this.uuid);
    },

    messages() {
      return this.getMessages(this.uuid, this.currentProviderUuid);
    },

    state() {
      return this.enquiry ? getState(this.enquiry) : null;
    },

    provider() {
      return this.getProviderByUuid(this.providerUuid);
    },

    providerName() {
      if (isNil(this.provider)) {
        return '';
      }

      return `${this.provider.first_name} ${this.provider.last_name}`;
    },

    enquiryTitle() {
      return this.enquiry ? `${getEnquiryTitle(this.enquiry)}` : null;
    },

    returnAction() {
      return { title: this.$t('message.action.back_to_enquiry'), to: getEnquiryRoute(this.enquiry.uuid) };
    },

    previewsData() {
      return getPreviewsData(this.provider, this.overviews);
    },

    messagesData() {
      const messagesData = getMessagesData(this.messages).reverse();

      if (isEmpty(messagesData)) {
        return [];
      }

      const lastMessage = messagesData[0];

      if (this.newMessage && (this.newMessage.message.uuid === lastMessage.message.uuid)) {
        return messagesData.slice(1);
      }

      return messagesData;
    },

    /**
     * Verify if the requests are still loading (notice use of isNil and not isEmpty)
     */
    isLoading() {
      return isNil(this.state) || isNil(this.messages) || isNil(this.offers);
    },

    /*
     * The customer is anonymous if there are no offers in progress
     */
    displayAnonymousBanner() {
      if (this.isLoading) {
        return true;
      }

      return isEmpty(this.offers.filter((offer) => isInProgress(offer)));
    },

    blockChat() {
      // if still loading data (therefore use of isNil and not isEmpty)
      if (this.isLoading) {
        return false;
      }

      return isChatBlocked(this.state, this.messages.list, this.offers);
    },
  },

  methods: {
    ...mapActions({
      listOverviews: 'messages/listOverviews',
      listMessages: 'messages/listMessages',
      createMessage: 'messages/createMessage',
    }),

    toggleMobilePreviews() {
      this.mobile.showAllMessages = !this.mobile.showAllMessages;
    },

    sendMessage(messageContent) {
      const message = {
        enquiryUuid: this.uuid,
        providerUuid: this.providerUuid,
        message: messageContent,
      };

      this.newMessage = null;

      return this.createMessage(message)
        .then((newMessage) => this.showSentMessage(newMessage))
        .then(() => this.listMessages({ enquiryUuid: this.uuid, providerUuid: this.providerUuid }))
        .then(() => this.listOverviews());
    },

    showSentMessage(message) {
      trackMessageSent(this.enquiry, this.provider, message);
      const list = isEmpty(this.messages) ? [message] : [...this.messages.list, message];
      const messages = getMessagesData({ list, providerUuid: this.providerUuid });
      this.newMessage = messages[messages.length - 1];
    },

    onUpdateChat(uuid) {
      if (uuid === this.providerUuid) {
        this.toggleMobilePreviews();
      }

      this.currentProviderUuid = uuid;
      this.$router.push({ path: getMessagePath(this.uuid, uuid) });
    },
  },
};
